import CommonModal from "components/CommonModal";
import Loader from "components/Loader";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { State } from "state";
import { createRequest } from "state/request";

interface InternalCreateRequestProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (requestData: AccessRequestData) => void;
  currentUser?: string;
}

interface AccessRequestData {
  requestType: string;
  context: any;
  data: any;
}

type FormErrors = Partial<Record<"details" | "chainName" | "stores", string>>;

interface ComponentState {
  details: string;
  chainName: string;
  stores: string;
  errors: FormErrors;
  isSubmitting: boolean;
}

// Extracted styles as constants
const styles = {
  formContainer: {
    backgroundColor: "#F5F5F5",
    borderRadius: "10px",
    padding: "20px",
  },
  section: {
    marginBottom: "16px",
  },
  label: {
    display: "block",
    fontSize: "12px",
    fontWeight: 500,
    color: "#333",
    marginBottom: "8px",
  },
  radioContainer: {
    display: "flex",
    gap: "24px",
    alignItems: "center",
  },
  radioOption: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    gap: "8px",
  },
  radioOuterCircleChecked: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    border: "2px solid #E5B42F",
    backgroundColor: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.2s ease",
    boxSizing: "border-box" as const,
  },
  radioInnerDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#E5B42F",
    flexShrink: 0,
  },
  radioLabel: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#333",
    cursor: "pointer",
    userSelect: "none" as const,
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    fontSize: "12px",
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    color: "#333",
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
    boxSizing: "border-box" as const,
  },
  inputError: {
    width: "100%",
    padding: "10px 14px",
    fontSize: "12px",
    border: "1px solid #E01F29",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    color: "#333",
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
    boxSizing: "border-box" as const,
  },
  textarea: {
    width: "100%",
    padding: "10px 14px",
    fontSize: "12px",
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    color: "#333",
    resize: "vertical" as const,
    minHeight: "80px",
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
    boxSizing: "border-box" as const,
  },
  twoColumnRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginBottom: "16px",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "20px",
    alignItems: "center",
  },
  cancelButton: {
    padding: "8px 16px",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "#E5B42F",
    color: "#000",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
  },
  submitButton: {
    padding: "8px 16px",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "#E5B42F",
    color: "#000",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
  },
  submitButtonDisabled: {
    padding: "8px 16px",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "#E0E0E0",
    color: "#999",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "not-allowed",
  },
  errorText: {
    color: "#E01F29",
    fontSize: "12px",
    marginTop: "6px",
  },
  hiddenRadio: {
    position: "absolute" as const,
    opacity: 0,
    width: 0,
    height: 0,
  },
} as const;

type CombinedProps = InternalCreateRequestProps & PropsFromRedux;

class InternalCreateRequest extends React.Component<CombinedProps, ComponentState> {
  private requestType = "CHAIN_ACCESS";

  constructor(props: CombinedProps) {
    super(props);
    this.state = {
      details: "",
      chainName: "",
      stores: "",
      errors: {},
      isSubmitting: false,
    };
  }

  componentDidUpdate(prevProps: CombinedProps) {
    // Reset form when modal closes
    if (prevProps.isOpen && !this.props.isOpen) {
      this.setState({
        details: "",
        chainName: "",
        stores: "",
        errors: {},
        isSubmitting: false,
      });
    }
  }

  handleDetailsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { errors } = this.state;
    this.setState({ details: e.target.value });

    if (errors.details) {
      this.setState((prevState) => ({
        errors: { ...prevState.errors, details: "" }
      }));
    }
  };

  handleChainNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ chainName: e.target.value });
    if (this.state.errors.chainName) {
      this.setState((prevState) => ({
        errors: { ...prevState.errors, chainName: "" },
      }));
    }
  };

  handleStoresChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ stores: e.target.value });
    if (this.state.errors.stores) {
      this.setState((prevState) => ({
        errors: { ...prevState.errors, stores: "" },
      }));
    }
  };

  validateForm = (): boolean => {
    const { details, chainName, stores } = this.state;
    const newErrors: FormErrors = {};

    if (!chainName.trim()) {
      newErrors.chainName = "Please provide a chain name";
    }


    if (!details.trim()) {
      newErrors.details = "Please provide details for your request";
    }

    this.setState({ errors: newErrors });
    return Object.keys(newErrors).length === 0;
  };

  handleSubmit = async () => {
    if (!this.validateForm()) {
      return;
    }

    const { details, chainName, stores } = this.state;
    const { authUser, createRequest, onSubmit, onClose } = this.props;
    const userId = authUser?.id;
    const userEmail = authUser?.email;

    this.setState({ isSubmitting: true });

    try {
      // Prepare API payload
      const requestData: AccessRequestData = {
        requestType: this.requestType,
        context: {
          initiator_user_id: userId,
          initiator_email: userEmail,
          request_intiated_from: "REQUEST PAGE",
          payload_from: "REQUESTOR"
        },
        data: {
          [this.requestType]: [{
            chain_name: chainName.trim(),
            stores: stores.trim(),
            chain_description: details.trim()
          }]
        }
      };

      // Call API through Redux action
      await createRequest(requestData);

      onSubmit(requestData);
      onClose();
    } catch (error) {
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          details: "Failed to submit request. Please try again.",
        },
      }));
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const { isOpen, onClose } = this.props;
    const { details, chainName, stores, errors, isSubmitting } = this.state;

    const isFormValid = Boolean(details.trim() && chainName.trim());

    // Modal Footer
    const footer = (
      <div style={styles.buttonsContainer}>
        <button
          style={styles.cancelButton}
          onClick={onClose}
          type="button"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              display: "inline-flex",
              width: 20,
              height: 20,
              alignItems: "center",
              justifyContent: "center",
              visibility: isSubmitting ? "visible" : "hidden",
            }}
            aria-hidden={!isSubmitting}
          >
            {isSubmitting && <Loader height={20} width={20} custom={true} />}
          </span>
          <button
            style={{
              ...(isFormValid ? styles.submitButton : styles.submitButtonDisabled),
              opacity: isSubmitting ? 0.5 : 1,
              cursor: isSubmitting ? "not-allowed" : (!isFormValid ? "not-allowed" : "pointer"),
            }}
            onClick={this.handleSubmit}
            disabled={!isFormValid || isSubmitting}
            aria-label={isSubmitting ? "Submitting request" : "Submit request"}
            aria-busy={isSubmitting || undefined}
            type="button"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    );

    return (
      <CommonModal
        show={isOpen}
        onClose={onClose}
        title="Submit Request"
        subTitle="Request approval to map a chain to your project."
        width="800px"
        closeOnBackdropClick={!isSubmitting}
        footer={footer}
      >
        <div style={{ borderBottom: "1px solid #F0F2F5", marginBottom: "20px" }} />
        <div style={styles.section}>
          <label style={styles.label}>Select Type of Request</label>
          <div style={styles.radioContainer}>
            <div style={styles.radioOption}>
              <input
                type="radio"
                id="chain-mapping"
                name="requestType"
                value="CHAIN_ACCESS"
                checked={true}
                readOnly
                style={styles.hiddenRadio}
              />
              <div style={styles.radioOuterCircleChecked}>
                <div style={styles.radioInnerDot} />
              </div>
              <label htmlFor="chain-mapping" style={styles.radioLabel}>
                Chain Mapping Requests
              </label>
            </div>
          </div>
        </div>

        <div style={styles.formContainer}>
          {/* Chain Name & Stores row */}
          <div style={styles.twoColumnRow}>
            <div>
              <label htmlFor="chain-name-input" style={styles.label}>
                Chain Name <span style={{ color: "#E01F29" }}>*</span>
              </label>
              <input
                id="chain-name-input"
                type="text"
                value={chainName}
                onChange={this.handleChainNameChange}
                placeholder="Enter chain name..."
                style={errors.chainName ? styles.inputError : styles.input}
                disabled={isSubmitting}
              />
              {errors.chainName && (
                <p style={styles.errorText}>{errors.chainName}</p>
              )}
            </div>

            <div>
              <label htmlFor="stores-input" style={styles.label}>
                Stores
              </label>
              <input
                id="stores-input"
                type="text"
                value={stores}
                onChange={this.handleStoresChange}
                placeholder="Enter stores..."
                style={errors.stores ? styles.inputError : styles.input}
                disabled={isSubmitting}
              />
              {errors.stores && (
                <p style={styles.errorText}>{errors.stores}</p>
              )}
            </div>
          </div>

          {/* Details textarea */}
          <div style={styles.section}>
            <label htmlFor="details-textarea" style={styles.radioLabel}>
              Input Details <span style={{ color: "#E01F29" }}>*</span>
            </label>
            <textarea
              id="details-textarea"
              value={details}
              onChange={this.handleDetailsChange}
              placeholder="Please provide details for your request..."
              style={styles.textarea}
              disabled={isSubmitting}
            />
            {errors.details && <p style={styles.errorText}>{errors.details}</p>}
          </div>
        </div>
      </CommonModal>
    );
  }
}

const mapStateToProps = (state: State) => ({
  authUser: state.auth_user.content,
});

const mapDispatchToProps = {
  createRequest,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(InternalCreateRequest);